import React from 'react';

// --- 1. ERROR BOUNDARY COMPONENT ---
// Catches crashes in sub-components to prevent the white screen of death
export class ErrorBoundary extends React.Component {
  constructor(props) { 
    super(props); 
    this.state = { hasError: false, err: null }; 
  }
  
  static getDerivedStateFromError(error) { 
    return { hasError: true, err: error }; 
  }
  
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', color: '#dc2626', background: '#fef2f2', border: '1px solid #dc2626', margin: '20px', borderRadius: '8px' }}>
          <h3>⚠️ System Error</h3>
          <p>The application encountered an unexpected error.</p>
          <pre style={{background:'rgba(255,0,0,0.05)', padding:'10px', fontSize:'0.8rem', overflowX:'auto'}}>
             {this.state.err?.toString()}
          </pre>
          <button 
            onClick={() => window.location.reload()} 
            style={{ 
              padding: '10px 20px', 
              marginTop: '15px', 
              cursor: 'pointer', 
              background:'#dc2626', 
              color:'white', 
              border:'none', 
              borderRadius:'4px', 
              fontWeight:'bold' 
            }}
          >
            RELOAD SYSTEM
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// --- 2. REUSABLE TABLE COMPONENT ---
// Standardizes table look across Costing, Admin, and Reports
export const TableFull = ({ data, cols, onEdit, onDelete }) => (
  <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', marginTop: '15px', background:'white' }}>
    <table className="npd-table" style={{ margin: 0 }}>
      <thead>
        <tr>
          {cols.map(c => <th key={c}>{c.replace(/([A-Z])/g, ' $1').toUpperCase()}</th>)}
          {(onEdit || onDelete) && <th>ACTION</th>}
        </tr>
      </thead>
      <tbody>
        {data.map((row, i) => (
          <tr key={row.id || i}>
            {cols.map(c => <td key={c}>{row[c]}</td>)}
            {(onEdit || onDelete) && (
               <td>
                 {onEdit && <button onClick={() => onEdit(row)} className="btn-edit">Edit</button>}
                 {onDelete && <button onClick={() => onDelete(row.id)} className="btn-del">×</button>}
               </td>
            )}
          </tr>
        ))}
        {data.length === 0 && (
          <tr>
            <td colSpan={cols.length + (onEdit||onDelete?1:0)} style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>
              No Records Found
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

// --- 3. GLOBAL CSS STYLES ---
// Contains all layout, typography, and utility classes
export const styles = `
  /* BASE RESET */
  * { box-sizing: border-box; font-family: 'Inter', 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; }
  
  /* APP SHELL - Forces full screen with no outer scroll */
  html, body { 
    margin: 0; 
    padding: 0; 
    background-color: #f8fafc; 
    height: 100vh; 
    width: 100vw; 
    overflow: hidden; 
  }
  
  .erp-shell { 
    display: flex; 
    flex-direction: column; 
    height: 100%; 
    width: 100%; 
  }
  
  /* HEADER */
  .erp-header { 
    height: 60px; 
    background: #0f172a; 
    color: white; 
    display: flex; 
    align-items: center; 
    justify-content: space-between; 
    padding: 0 25px; 
    flex-shrink: 0; 
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); 
    z-index: 20;
  }
  .brand-zone { display: flex; flex-direction: column; }
  .logo { font-size: 1.2rem; fontWeight: 800; letter-spacing: 0.5px; color: #38bdf8; }
  .v-tag { font-size: 0.7rem; color: #94a3b8; font-weight: 500; }
  
  /* NAVIGATION */
  .nav-bar button {
    background: transparent; 
    border: none; 
    color: #cbd5e1; 
    padding: 0 15px; 
    height: 60px; 
    cursor: pointer; 
    font-weight: 600; 
    font-size: 0.85rem; 
    transition: all 0.2s; 
    border-bottom: 3px solid transparent;
  }
  .nav-bar button:hover { color: white; background: rgba(255,255,255,0.05); }
  .nav-bar button.active { color: #38bdf8; border-bottom: 3px solid #38bdf8; background: rgba(255,255,255,0.05); }

  /* VIEW STAGE & LAYOUTS */
  .view-stage { flex: 1; overflow: hidden; position: relative; display: flex; flex-direction: column; }
  .layout-master { display: flex; width: 100%; height: 100%; overflow: hidden; }
  
  /* SIDEBAR */
  .side-rail { 
    width: 260px; 
    background: white; 
    border-right: 1px solid #e2e8f0; 
    display: flex; 
    flex-direction: column; 
    padding: 15px; 
    overflow-y: auto; 
    flex-shrink: 0; 
  }
  .side-rail button {
    text-align: left; padding: 12px 15px; border: none; background: transparent; 
    color: #475569; cursor: pointer; border-radius: 8px; margin-bottom: 4px; 
    font-weight: 500; transition: all 0.2s; font-size: 0.9rem;
  }
  .side-rail button:hover { background: #f1f5f9; color: #0f172a; }
  .side-rail button.active { background: #eff6ff; color: #2563eb; font-weight: 700; border-left: 4px solid #2563eb; }

  /* MAIN CONTENT - SCROLLABLE */
  .content-area { 
    flex: 1; 
    padding: 25px; 
    overflow-y: auto; /* Fixes Vertical Scroll */
    overflow-x: hidden; 
    background: #f8fafc; 
    display: flex; 
    flex-direction: column;
    padding-bottom: 150px; /* Buffer for floating buttons */
  }

  /* MODERN CARD UI */
  .card { 
    background: white; border-radius: 10px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); 
    padding: 20px; margin-bottom: 25px; border: 1px solid #e2e8f0; flex-shrink: 0;
  }
  .card h4 { 
    margin-top: 0; color: #0f172a; border-bottom: 1px solid #f1f5f9; 
    padding-bottom: 12px; margin-bottom: 20px; font-size: 1rem; 
    font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;
  }
  
  /* MODERN E-COMMERCE GALLERY GRID */
  .gallery-grid {
     display: grid;
     grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
     gap: 20px;
  }
  .modern-card {
     background: white; border: 1px solid #e2e8f0; border-radius: 12px;
     overflow: hidden; transition: transform 0.2s, box-shadow 0.2s;
     display: flex; flex-direction: column; cursor: pointer;
  }
  .modern-card:hover {
     transform: translateY(-4px);
     box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
     border-color: #cbd5e1;
  }

  /* UTILITY: HORIZONTAL SCROLL CONTAINER (Fixes Gantt/Tables) */
  .scroll-x-container {
     overflow-x: auto;
     white-space: nowrap;
     padding-bottom: 10px;
     width: 100%;
     -webkit-overflow-scrolling: touch;
  }

  /* FORMS & GRIDS */
  .grid-2 { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 20px; }
  .grid-3 { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 20px; }
  .grid-4 { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 15px; }
  
  .form-row { display: flex; gap: 15px; align-items: flex-end; flex-wrap: wrap; margin-bottom: 20px; }
  .field { display: flex; flex-direction: column; gap: 6px; flex: 1; min-width: 140px; }
  .field label { font-size: 0.75rem; font-weight: 700; color: #64748b; text-transform: uppercase; }
  .field input, .field select { 
    padding: 10px; border: 1px solid #cbd5e1; border-radius: 6px; 
    font-size: 0.9rem; width: 100%; transition: all 0.2s; background: #fff;
  }
  .field input:focus, .field select:focus { border-color: #3b82f6; outline: none; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }

  /* BUTTONS & BADGES */
  .btn-p { 
    background: #2563eb; color: white; border: none; padding: 10px 20px; 
    border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 0.9rem; 
    transition: all 0.2s; white-space: nowrap; box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  }
  .btn-p:hover { background: #1d4ed8; transform: translateY(-1px); }
  .btn-edit { background: #e0f2fe; color: #0284c7; border: none; padding: 6px 10px; border-radius: 4px; cursor: pointer; font-size: 0.75rem; margin-right: 5px; font-weight: 600; }
  .btn-del { background: #fee2e2; color: #dc2626; border: none; padding: 6px 10px; border-radius: 4px; cursor: pointer; font-size: 0.75rem; font-weight: 600; }
  .badge { display: inline-block; padding: 5px 10px; background: #f0f9ff; color: #0369a1; border-radius: 9999px; font-weight: 600; font-size: 0.75rem; border: 1px solid #bae6fd; }
  
  /* TABLES & HEADERS */
  .npd-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; min-width: 600px; }
  .npd-table th { text-align: left; padding: 12px 15px; background: #f1f5f9; border-bottom: 2px solid #e2e8f0; color: #475569; font-size: 0.75rem; font-weight: 700; text-transform: uppercase; }
  .npd-table td { padding: 12px 15px; border-bottom: 1px solid #f1f5f9; color: #334155; }
  .npd-table tr:hover { background: #f8fafc; }
  
  .fob-header { 
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%); color: white; 
    padding: 20px; border-radius: 12px; margin-bottom: 25px; 
    display: flex; justify-content: space-between; align-items: center; 
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
  .fob-val { font-size: 1.8rem; font-weight: 800; color: #4ade80; }
  .hub-jump { cursor: pointer; color: #2563eb; text-decoration: none; font-weight: 600; border-bottom: 1px dashed #2563eb; }
  .hub-jump:hover { color: #1d4ed8; background: #eff6ff; border-bottom-style: solid; }
  
  /* SCROLLBAR CUSTOMIZATION */
  ::-webkit-scrollbar { width: 8px; height: 8px; }
  ::-webkit-scrollbar-track { background: #f1f5f9; }
  ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
  ::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
`;